import styled from "styled-components";

import {
    Button as StyledButton,
    InlineButton as StyledInlineButton,
} from "components/atoms/Button";
import { Input as StyledInput } from "components/atoms/Input";
import { device } from "utils/sizes";
import { colourScheme } from "utils/styling/colours";
import {
    Label as TypoLabel,
    SubTitle as StyledTitle,
    Text,
} from "utils/styling/typography.styled";

export const UploadForm = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    flex-direction: column;
    ${device.lg} {
        justify-content: start;
        flex-direction: row;
        gap: 1rem;
        align-items: flex-start;
    }
`;

export const FormContainer = styled.div<{ $hasImages: boolean }>`
    width: 100%;
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    transition: width 0.3s ease-in-out;
    align-items: flex-start;
    ${device.lg} {
        padding: 1rem;
        width: ${({ $hasImages }) => ($hasImages ? "50%" : "100%")};
    }
`;

export const ImageViewerContainer = styled.div<{ $hasImages: boolean }>`
    display: ${({ $hasImages }) => ($hasImages ? "flex" : "none")};
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    height: auto;
    padding: 1rem;
    box-sizing: border-box;
    flex-grow: 1;
`;

export const Form = styled.form`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    background-color: ${colourScheme.primaryDark};
    border-radius: 0.5rem;
    box-shadow: 0 0.625rem 1.25rem rgba(0, 0, 0, 0.5);
    width: 100%;
    padding: 0.5rem;
    ${device.sm} {
        width: auto;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        padding: 1rem;
    }
`;

export const FormGroup = styled.div<{ $fullWidth?: boolean }>`
    width: 100%;
    display: flex;
    align-items: stretch;
    justify-content: center;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem 0 0 0;
    grid-column: ${({ $fullWidth }) => ($fullWidth ? "span 2" : "span 1")};
`;

export const InlineInput = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
`;

export const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

export const LabelWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

export const InlineButton = styled(StyledInlineButton)`
    margin-left: auto;
    opacity: 0.5;
    cursor: pointer;
`;

export const Input = styled(StyledInput)`
    flex-grow: 1;
`;

export const Icon = styled.div`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    width: 1.5rem;
    cursor: pointer;
    position: relative;
    margin-left: 0.5rem;
    svg,
    img {
        opacity: 0.5;
        height: 100%;
        width: 100%;
    }
    & > span {
        padding: 0.25rem;
        color: ${colourScheme.secondary};
    }
`;

export const Label = styled(TypoLabel)`
    display: inline-flex;
    align-items: center;
`;

export const GenCounterLabel = styled.div`
    position: absolute;
    bottom: -2rem;
    right: 0.25rem;

    & > span {
        font-weight: 600;
    }
`;

export const UnderlinedText = styled(Text)`
    text-decoration: underline;
    color: ${colourScheme.white} !important;
    opacity: 1;
`;

export const Button = styled(StyledButton)`
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    gap: 0.5rem;
    white-space: wrap;
    text-overflow: ellipsis;
    width: auto;
    max-width: 100%;
`;

export const AccordionContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    box-sizing: border-box;
    flex-direction: column;
`;

export const Title = styled(StyledTitle)`
    padding: 1rem;
    display: flex;
    justify-content: center;
    text-align: center;
    flex-wrap: wrap;
    width: 100%;
    font-size: 1.25rem;

    ${device.md} {
        font-size: 2rem;
        width: 75%;
    }
`;

export const GoldCost = styled.span`
    color: gold;
    font-size: 0.9em;
    margin-left: 0.5rem;
`;

export const TotalGoldCost = styled.div`
    margin-top: 1em;
    font-weight: bold;
    color: gold;
    font-size: 1.2em;
`;

export const TabsContainer = styled.div`
    grid-column: span 2;
    width: 100%;
    position: relative;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 0.25rem;
`;

export const TabsWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 0.25rem;
    ${device.sm} {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
    }
`;

export const ImagePreview = styled.div`
    margin-top: 10px;
    img {
        max-width: 100%;
        height: auto;
        border: 1px solid #ccc;
        border-radius: 4px;
    }
`;

export const RemoveButton = styled(StyledInlineButton)`
    margin-right: auto;
    opacity: 0.5;
    cursor: pointer;
`;

export const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    /* Optional: Add spacing between the child components */
    & > *:not(:last-child) {
        margin-right: 1rem; /* Adjust the margin as needed */
    }
`;

export const ArtStyleSelectionContainer = styled.div`
    flex-grow: 1; // Allow this component to take up the remaining space
`;
